// Apple — the Academy waterfall, one month at a time, plus the annual statement.
// Moved from pages/Books.tsx with behavior unchanged: same queries, same
// `books.*` edge actions, same CSV. New here: every write is gated by
// books_can_edit, and an editor can re-post a closed month to the ledger.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth, useBooksAccess } from '../../contexts/AuthContext';
import { call } from '../../lib/backbarApi';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dollars, monthFirst, monthKey, monthName, toCents } from '../../lib/format';
import { reports } from '../../lib/ledger/api';
import { ReadOnlyNote, localIso } from './shared';

type MonthRow = {
  month: string;
  gross_cents: number;
  commission_cents: number;
  refunds_cents: number;
  net_cents: number | null;
  apple_paid_at: string | null;
  source: string | null;
  closed_at: string | null;
};
type Cost = { id: string; month: string; vendor: string; amount_cents: number; share_pct: number | null; allocated_cents: number | null; source: string | null; mercury_transaction_id: string | null; created_at: string };
type Dist = { id: string; month: string; payee: 'leah' | 'laura'; share_pct: number; amount_cents: number; status: 'retained' | 'owed' | 'paid'; paid_at: string | null; method: string | null; reference: string | null; mercury_request_id: string | null };
type Adj = { id: string; month: string; amount_cents: number; reason: string; created_at: string };
type Settings = { shares: Record<string, number>; vendor_shares: Record<string, number> };

const STATEMENT_HEADLINE =
  'Rootegra LLC — revenue and member distributions.';

function vendorWords(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AppleTab() {
  const [view, setView] = useState<'month' | 'year'>('month');
  const [month, setMonth] = useState(monthKey());
  const [year, setYear] = useState(String(new Date().getFullYear()));

  return (
    <>
      <div className="lp-row lp-no-print" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
        <p className="lp-sub" style={{ margin: 0 }}>
          Apple's deposits post to Rootegra's operating cash account.
        </p>
        <div className="lp-tabs" aria-label="Apple view">
          <button type="button" className={view === 'month' ? 'on' : ''} onClick={() => setView('month')}>Month</button>
          <button type="button" className={view === 'year' ? 'on' : ''} onClick={() => setView('year')}>Year</button>
        </div>
      </div>
      {view === 'month' ? (
        <MonthView month={month} setMonth={setMonth} />
      ) : (
        <YearView year={year} setYear={setYear} />
      )}
    </>
  );
}

// ── Month ────────────────────────────────────────────────────────────────────
function MonthView({ month, setMonth }: { month: string; setMonth: (m: string) => void }) {
  const { user } = useAuth();
  const { canEdit } = useBooksAccess();
  const first = monthFirst(month);
  const [row, setRow] = useState<MonthRow | null | undefined>(undefined);
  const [costs, setCosts] = useState<Cost[]>([]);
  const [dists, setDists] = useState<Dist[]>([]);
  const [adjs, setAdjs] = useState<Adj[]>([]);
  const [settings, setSettings] = useState<Settings>({ shares: { leah: 51, laura: 49 }, vendor_shares: {} });
  const [loadError, setLoadError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<{ where: string; error: unknown } | null>(null);
  const [ledger, setLedger] = useState<Record<string, unknown> | null>(null);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    const [m, c, d, a, s] = await Promise.all([
      supabase.from('books_months').select('*').eq('month', first).maybeSingle(),
      supabase.from('books_costs').select('*').eq('month', first).order('created_at'),
      supabase.from('books_distributions').select('*').eq('month', first).order('payee'),
      supabase.from('books_adjustments').select('*').eq('month', first).order('created_at'),
      supabase.from('books_settings').select('key, value'),
    ]);
    const bad = [m, c, d, a, s].find((r) => r.error);
    if (bad?.error) {
      setLoadError(bad.error);
      return;
    }
    setRow((m.data as MonthRow | null) ?? null);
    setCosts((c.data ?? []) as Cost[]);
    setDists((d.data ?? []) as Dist[]);
    setAdjs((a.data ?? []) as Adj[]);
    const st: Settings = { shares: { leah: 51, laura: 49 }, vendor_shares: {} };
    for (const r of (s.data ?? []) as { key: string; value: unknown }[]) {
      if (r.key === 'shares') st.shares = r.value as Record<string, number>;
      if (r.key === 'vendor_shares') st.vendor_shares = r.value as Record<string, number>;
    }
    setSettings(st);
  }, [first]);

  useEffect(() => {
    setRow(undefined);
    setDone(null);
    setErr(null);
    setLedger(null);
    void load();
  }, [load]);

  const closed = Boolean(row?.closed_at);
  const frozen = dists.some((d) => d.status === 'paid');
  const net = row ? (row.net_cents ?? row.gross_cents - row.commission_cents - row.refunds_cents) : 0;
  const allocated = costs.reduce((s, c) => s + Number(c.allocated_cents ?? 0), 0);
  const adjTotal = adjs.reduce((s, a) => s + Number(a.amount_cents), 0);
  const distributable = net - allocated + adjTotal;
  const leahPct = Number(settings.shares.leah ?? 51);
  const lauraPct = Number(settings.shares.laura ?? 49);
  const leahCents = Math.round(distributable * leahPct / 100);
  const lauraCents = distributable - leahCents;
  const leahDist = dists.find((d) => d.payee === 'leah') ?? null;
  const lauraDist = dists.find((d) => d.payee === 'laura') ?? null;

  async function act(where: string, label: string, fn: () => Promise<void>) {
    setErr(null);
    setDone(null);
    try {
      await fn();
      setDone(label);
      await load();
    } catch (error) {
      setErr({ where, error });
    }
  }

  const who = `manual: ${user?.email ?? 'admin'}`;

  async function ensureMonthRow() {
    if (row) return;
    const { error } = await supabase.from('books_months').insert({ month: first, source: who });
    if (error) throw error;
  }

  // Editors only, closed month only: rebuild this month's ledger entries from
  // the waterfall. Idempotent on the database side; a frozen month is left alone.
  async function postToLedgerAgain() {
    setErr(null);
    setDone(null);
    setLedger(null);
    setPosting(true);
    try {
      const r = await reports.postAppleMonth(first);
      setLedger(r ?? {});
    } catch (error) {
      setErr({ where: 'ledger', error });
    } finally {
      setPosting(false);
    }
  }

  const monthPicker = (
    <div className="lp-no-print" style={{ marginBottom: 18 }}>
      <div className="lp-row">
        <label className="lp-field-label" htmlFor="books-month">Month</label>
        <input id="books-month" type="month" className="lp-inp" style={{ width: 'auto', minWidth: 200 }} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <span className="lp-sub" style={{ margin: 0 }}>
          {row === undefined ? '' : closed ? `Closed ${day(row?.closed_at)}` : row ? 'Open' : 'Nothing entered yet'}
          {frozen ? ' · paid out, so the numbers are frozen' : ''}
        </span>
      </div>
      <p className="lp-field-hint" style={{ margin: '8px 0 0' }}>
        Closing a month posts it to the ledger. Costs, adjustments, and payouts post on their own.
      </p>
    </div>
  );

  if (loadError) {
    return (
      <>
        {monthPicker}
        <ErrorNote error={loadError} />
      </>
    );
  }
  if (row === undefined) {
    return (
      <>
        {monthPicker}
        <Empty>Loading…</Empty>
      </>
    );
  }

  return (
    <>
      {monthPicker}
      {!canEdit && <ReadOnlyNote />}
      {done && <Note tone="good">{done}</Note>}

      {/* 1 · What came in */}
      <Section title="1. What came in">
        <div className="lp-panel">
          {row ? (
            <>
              <dl className="lp-kv">
                <dt>Gross sales</dt><dd className="lp-money">{dollars(row.gross_cents)}</dd>
                <dt>Apple's commission</dt><dd className="lp-money">− {dollars(row.commission_cents)}</dd>
                <dt>Refunds</dt><dd className="lp-money">− {dollars(row.refunds_cents)}</dd>
                <dt>Net proceeds</dt><dd className="lp-money"><strong>{dollars(net)}</strong></dd>
                <dt>Apple paid on</dt><dd>{row.apple_paid_at ? day(row.apple_paid_at) : 'Not yet'}</dd>
                <dt>Source</dt><dd>{row.source ?? '—'}</dd>
              </dl>
            </>
          ) : (
            <p style={{ margin: '0 0 12px', fontSize: 17 }}>Not pulled yet.</p>
          )}
          {err?.where === 'apple' && (
            <ErrorNote error={err.error} notConfigured="Apple finance reports aren't connected yet — enter the numbers below." />
          )}
          {!closed && (
            <div className="lp-actions">
              <button
                type="button"
                className="lp-btn"
                disabled={!canEdit}
                onClick={() =>
                  act('apple', 'Pulled from Apple.', async () => {
                    await call('books.pull_apple', { month });
                  })
                }
              >
                Pull from Apple
              </button>
            </div>
          )}
          {!closed && <ProceedsForm row={row} first={first} who={who} disabled={!canEdit} onSaved={() => act('proceeds', 'Saved.', async () => {})} onError={(e) => setErr({ where: 'proceeds', error: e })} />}
          {err?.where === 'proceeds' && <ErrorNote error={err.error} />}
        </div>
      </Section>

      {/* 2 · What it cost */}
      <Section title="2. What it cost">
        <div className="lp-panel">
          {costs.length === 0 ? (
            <Empty>No shared costs entered for {monthName(first)}.</Empty>
          ) : (
            <div className="lp-tbl-wrap">
              <table className="lp-tbl">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th className="num">Bill</th>
                    <th className="num">Rootegra share</th>
                    <th className="num">Rootegra pays</th>
                    <th>From</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map((c) => (
                    <tr key={c.id}>
                      <td>{vendorWords(c.vendor)}</td>
                      <td className="num">{dollars(c.amount_cents)}</td>
                      <td className="num">{c.share_pct ?? 100}%</td>
                      <td className="num">{dollars(c.allocated_cents)}</td>
                      <td className="muted">{c.mercury_transaction_id ? 'Bank' : c.source ?? 'Typed in'}</td>
                      <td>
                        {!frozen && (
                          <ConfirmButton
                            label="Remove"
                            disabled={!canEdit}
                            sentence={`Remove the ${vendorWords(c.vendor)} bill of ${dollars(c.amount_cents)} from ${monthName(first)}.`}
                            yes="Yes, remove it"
                            onConfirm={() =>
                              act('costs', 'Removed.', async () => {
                                const { error } = await supabase.from('books_costs').delete().eq('id', c.id);
                                if (error) throw error;
                              })
                            }
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>Rootegra's share of costs</td>
                    <td></td>
                    <td></td>
                    <td className="num">{dollars(allocated)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {err?.where === 'bank' && <ErrorNote error={err.error} notConfigured="The bank isn't connected yet — add each cost below." />}
          {err?.where === 'costs' && <ErrorNote error={err.error} />}
          {!frozen && (
            <>
              <div className="lp-actions">
                <button
                  type="button"
                  className="lp-btn"
                  disabled={!canEdit}
                  onClick={() =>
                    act('bank', 'Pulled from the bank.', async () => {
                      await call('books.pull_costs', { month });
                    })
                  }
                >
                  Pull from the bank
                </button>
              </div>
              <CostForm
                vendorShares={settings.vendor_shares}
                disabled={!canEdit}
                onAdd={(vendor, amountCents, sharePct) =>
                  act('costs', 'Cost added.', async () => {
                    await ensureMonthRow();
                    const { error } = await supabase.from('books_costs').insert({
                      month: first, vendor, amount_cents: amountCents, share_pct: sharePct, source: who,
                    });
                    if (error) throw error;
                  })
                }
              />
            </>
          )}
        </div>
      </Section>

      {/* 3 · The split */}
      <Section title="3. The split">
        <div className="lp-panel">
          <p className="lp-formula">
            Net proceeds {dollars(net)} minus Rootegra's share of costs {dollars(allocated)}
            {adjTotal !== 0 ? ` ${adjTotal > 0 ? 'plus' : 'minus'} adjustments ${dollars(Math.abs(adjTotal))}` : ''}
            {' '}= <strong className="lp-money">{dollars(distributable)}</strong> to split.
          </p>
          <div className="lp-cards">
            <div className="lp-card">
              <div className="lp-big-money">{dollars(closed && leahDist ? leahDist.amount_cents : leahCents)}</div>
              <div className="lp-card-title">Leah · {closed && leahDist ? leahDist.share_pct : leahPct}%</div>
              <div className="lp-card-line">Sent to Leah as a member distribution.</div>
            </div>
            <div className="lp-card">
              <div className="lp-big-money">{dollars(closed && lauraDist ? lauraDist.amount_cents : lauraCents)}</div>
              <div className="lp-card-title">Laura · {closed && lauraDist ? lauraDist.share_pct : lauraPct}%</div>
              <div className="lp-card-line">Kept in the holding account as Laura's distribution.</div>
            </div>
          </div>
          {err?.where === 'close' && <ErrorNote error={err.error} />}
          {closed ? (
            <Note>
              Closed {day(row?.closed_at)}. {frozen ? 'A payout is recorded, so the numbers are frozen for good.' : 'To change a number, add an adjustment below with a reason, then close again.'}
            </Note>
          ) : (
            <div className="lp-actions">
              <ConfirmButton
                label="Close this month"
                tone="primary"
                disabled={!row || !canEdit}
                sentence="Closing locks the numbers and posts the month to the ledger. You can still add an adjustment with a reason."
                yes="Yes, close it"
                onConfirm={() =>
                  act('close', `${monthName(first)} is closed.`, async () => {
                    await call('books.close_month', { month });
                  })
                }
              />
              {!row && <span className="lp-sub" style={{ margin: 0 }}>Enter what came in first.</span>}
            </div>
          )}
          {closed && !frozen && (
            <div className="lp-actions">
              <ConfirmButton
                label="Close again with the adjustments"
                disabled={!canEdit}
                sentence="This recalculates the split with every adjustment on record."
                yes="Yes, close again"
                onConfirm={() =>
                  act('close', 'Closed again.', async () => {
                    await call('books.close_month', { month });
                  })
                }
              />
            </div>
          )}
          {closed && canEdit && (
            <div className="lp-actions">
              <button type="button" className="lp-btn lp-btn--quiet" disabled={posting} onClick={() => void postToLedgerAgain()}>
                {posting ? 'Posting…' : 'Post to ledger again'}
              </button>
              <span className="lp-field-hint">Rebuilds this month's ledger entries from the numbers above. Safe to repeat.</span>
            </div>
          )}
          {err?.where === 'ledger' && <ErrorNote error={err.error} />}
          {ledger && (
            <Note tone="good">
              Posted {monthName(first)} to the ledger again.
              {ledgerSummary(ledger) && <> {ledgerSummary(ledger)}</>}
            </Note>
          )}
        </div>
      </Section>

      {/* 4 · Paid */}
      <Section title="4. Paid">
        <div className="lp-panel">
          {!closed || dists.length === 0 ? (
            <Empty>Close the month to see the two distribution rows.</Empty>
          ) : (
            <>
              {err?.where === 'payout' && <ErrorNote error={err.error} notConfigured="Mercury isn't connected yet. Record the payout by hand instead." />}
              <ul className="lp-list">
                {leahDist && (
                  <li className="lp-item" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="lp-item-main">
                      <div className="lp-item-title">Leah · <span className="lp-money">{dollars(leahDist.amount_cents)}</span></div>
                      <div className="lp-item-line">
                        {leahDist.status === 'paid'
                          ? `Paid ${day(leahDist.paid_at)}${leahDist.method ? ` by ${leahDist.method}` : ''}${leahDist.reference ? ` · ${leahDist.reference}` : ''}`
                          : leahDist.mercury_request_id
                            ? 'Waiting for approval in Mercury.'
                            : 'Owed. Not paid yet.'}
                      </div>
                    </div>
                    {leahDist.status !== 'paid' && (
                      <PayoutActions
                        dist={leahDist}
                        month={month}
                        disabled={!canEdit}
                        onAct={(label, fn) => act('payout', label, fn)}
                      />
                    )}
                  </li>
                )}
                {lauraDist && (
                  <li className="lp-item">
                    <div className="lp-item-main">
                      <div className="lp-item-title">Laura · <span className="lp-money">{dollars(lauraDist.amount_cents)}</span></div>
                      <div className="lp-item-line">Retained in the holding account. Nothing to send.</div>
                    </div>
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      </Section>

      {/* 5 · Adjustments */}
      <Section title="5. Adjustments">
        <div className="lp-panel">
          {adjs.length === 0 ? <Empty>No adjustments for {monthName(first)}.</Empty> : (
            <ul className="lp-list">
              {adjs.map((a) => (
                <li key={a.id} className="lp-item">
                  <div className="lp-item-main">
                    <div className="lp-item-title lp-money">{a.amount_cents < 0 ? '− ' : '+ '}{dollars(Math.abs(a.amount_cents))}</div>
                    <div className="lp-item-line">{a.reason} · {day(a.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {err?.where === 'adj' && <ErrorNote error={err.error} />}
          <AdjustmentForm
            disabled={!canEdit}
            onAdd={(cents, reason) =>
              act('adj', 'Adjustment added.', async () => {
                await ensureMonthRow();
                const { error } = await supabase.from('books_adjustments').insert({ month: first, amount_cents: cents, reason });
                if (error) throw error;
              })
            }
          />
        </div>
      </Section>
    </>
  );
}

/** "entries: 3 · total: 1,234.00" from whatever the RPC hands back; nothing when it is empty. */
function ledgerSummary(r: Record<string, unknown>): string {
  const parts = Object.entries(r)
    .filter(([, v]) => v != null && typeof v !== 'object')
    .map(([k, v]) => {
      const label = k.replace(/_cents$/, '').replace(/_/g, ' ');
      const val = /_cents$/.test(k) && typeof v === 'number' ? dollars(v) : String(v);
      return `${label}: ${val}`;
    });
  return parts.join(' · ');
}

function ProceedsForm({ row, first, who, disabled, onSaved, onError }: { row: MonthRow | null; first: string; who: string; disabled?: boolean; onSaved: () => void; onError: (e: unknown) => void }) {
  const [open, setOpen] = useState(!row);
  const [gross, setGross] = useState(row ? (row.gross_cents / 100).toFixed(2) : '');
  const [commission, setCommission] = useState(row ? (row.commission_cents / 100).toFixed(2) : '');
  const [refunds, setRefunds] = useState(row ? (row.refunds_cents / 100).toFixed(2) : '');
  const [paidOn, setPaidOn] = useState(row?.apple_paid_at ? row.apple_paid_at.slice(0, 10) : '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOpen(!row);
    setGross(row ? (row.gross_cents / 100).toFixed(2) : '');
    setCommission(row ? (row.commission_cents / 100).toFixed(2) : '');
    setRefunds(row ? (row.refunds_cents / 100).toFixed(2) : '');
    setPaidOn(row?.apple_paid_at ? row.apple_paid_at.slice(0, 10) : '');
  }, [row]);

  const g = toCents(gross) ?? 0;
  const c = toCents(commission) ?? 0;
  const r = toCents(refunds) ?? 0;

  if (!open) {
    return (
      <div className="lp-actions">
        <button type="button" className="lp-btn" disabled={disabled} onClick={() => setOpen(true)}>Change the numbers</button>
      </div>
    );
  }
  return (
    <div className="lp-form" style={{ marginTop: 16 }}>
      <p className="lp-field-hint" style={{ marginTop: 0 }}>From App Store Connect, Payments and Financial Reports, for this fiscal month.</p>
      <div className="lp-form-grid">
        <Field label="Gross sales"><input className="lp-inp" inputMode="decimal" value={gross} disabled={disabled} onChange={(e) => setGross(e.target.value)} placeholder="0.00" /></Field>
        <Field label="Apple's commission"><input className="lp-inp" inputMode="decimal" value={commission} disabled={disabled} onChange={(e) => setCommission(e.target.value)} placeholder="0.00" /></Field>
        <Field label="Refunds"><input className="lp-inp" inputMode="decimal" value={refunds} disabled={disabled} onChange={(e) => setRefunds(e.target.value)} placeholder="0.00" /></Field>
        <Field label="Apple paid on" hint="Leave blank until the deposit lands."><input className="lp-inp" type="date" value={paidOn} disabled={disabled} onChange={(e) => setPaidOn(e.target.value)} /></Field>
      </div>
      <p className="lp-formula">Net proceeds: <strong className="lp-money">{dollars(g - c - r)}</strong></p>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={disabled || busy || toCents(gross) == null}
          onClick={async () => {
            setBusy(true);
            try {
              const { error } = await supabase.from('books_months').upsert({
                month: first,
                gross_cents: g,
                commission_cents: c,
                refunds_cents: r,
                net_cents: g - c - r,
                apple_paid_at: paidOn ? new Date(`${paidOn}T12:00:00`).toISOString() : null,
                source: who,
                updated_at: new Date().toISOString(),
              });
              if (error) throw error;
              setOpen(false);
              onSaved();
            } catch (e) {
              onError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save what came in'}
        </button>
        {row && <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setOpen(false)}>Cancel</button>}
      </div>
    </div>
  );
}

function CostForm({ vendorShares, disabled, onAdd }: { vendorShares: Record<string, number>; disabled?: boolean; onAdd: (vendor: string, amountCents: number, sharePct: number) => Promise<void> | void }) {
  const keys = Object.keys(vendorShares);
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState(keys[0] ?? 'other');
  const [other, setOther] = useState('');
  const [amount, setAmount] = useState('');
  const [share, setShare] = useState(String(vendorShares[keys[0]] ?? 100));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (vendor !== 'other') setShare(String(vendorShares[vendor] ?? 100));
  }, [vendor, vendorShares]);

  if (!open) {
    return (
      <div className="lp-actions">
        <button type="button" className="lp-btn" disabled={disabled} onClick={() => setOpen(true)}>Add a cost</button>
      </div>
    );
  }
  const cents = toCents(amount);
  const pct = Number(share);
  const name = vendor === 'other' ? other.trim() : vendor;
  return (
    <div className="lp-form" style={{ marginTop: 16 }}>
      <div className="lp-form-grid">
        <Field label="Vendor">
          <select className="lp-sel" value={vendor} disabled={disabled} onChange={(e) => setVendor(e.target.value)}>
            {keys.map((k) => <option key={k} value={k}>{vendorWords(k)}</option>)}
            <option value="other">Other</option>
          </select>
        </Field>
        {vendor === 'other' && (
          <Field label="Vendor name"><input className="lp-inp" value={other} disabled={disabled} onChange={(e) => setOther(e.target.value)} /></Field>
        )}
        <Field label="Whole bill"><input className="lp-inp" inputMode="decimal" placeholder="0.00" value={amount} disabled={disabled} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Rootegra's share %" hint="Filled in from the vendor. Change it if this bill is different.">
          <input className="lp-inp" inputMode="numeric" value={share} disabled={disabled} onChange={(e) => setShare(e.target.value)} />
        </Field>
      </div>
      <p className="lp-formula">Rootegra pays <strong className="lp-money">{dollars(cents != null && Number.isFinite(pct) ? Math.round(cents * pct / 100) : null)}</strong></p>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={disabled || busy || cents == null || !name || !Number.isFinite(pct) || pct < 0 || pct > 100}
          onClick={async () => {
            setBusy(true);
            try {
              await onAdd(name, cents!, pct);
              setAmount('');
              setOther('');
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Add the cost'}
        </button>
        <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function AdjustmentForm({ disabled, onAdd }: { disabled?: boolean; onAdd: (cents: number, reason: string) => Promise<void> | void }) {
  const [open, setOpen] = useState(false);
  const [sign, setSign] = useState<'+' | '-'>('+');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <div className="lp-actions">
        <button type="button" className="lp-btn" disabled={disabled} onClick={() => setOpen(true)}>Add an adjustment</button>
      </div>
    );
  }
  const cents = toCents(amount);
  return (
    <div className="lp-form" style={{ marginTop: 16 }}>
      <div className="lp-form-grid">
        <Field label="Direction">
          <select className="lp-sel" value={sign} disabled={disabled} onChange={(e) => setSign(e.target.value as '+' | '-')}>
            <option value="+">Add to the month</option>
            <option value="-">Take from the month</option>
          </select>
        </Field>
        <Field label="Amount"><input className="lp-inp" inputMode="decimal" placeholder="0.00" value={amount} disabled={disabled} onChange={(e) => setAmount(e.target.value)} /></Field>
      </div>
      <Field label="Reason" hint="Required. It prints on the annual statement.">
        <input className="lp-inp" value={reason} disabled={disabled} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="lp-row">
        <button
          type="button"
          className="lp-btn lp-btn--primary"
          disabled={disabled || busy || cents == null || cents === 0 || !reason.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              await onAdd(sign === '-' ? -Math.abs(cents!) : Math.abs(cents!), reason.trim());
              setAmount('');
              setReason('');
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Add the adjustment'}
        </button>
        <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function PayoutActions({ dist, month, disabled, onAct }: { dist: Dist; month: string; disabled?: boolean; onAct: (label: string, fn: () => Promise<void>) => Promise<void> }) {
  const [recording, setRecording] = useState(false);
  const [date, setDate] = useState(localIso());
  const [method, setMethod] = useState('Mercury');
  const [reference, setReference] = useState('');

  return (
    <div style={{ marginTop: 10 }}>
      {!recording ? (
        <div className="lp-row">
          <button type="button" className="lp-btn" disabled={disabled} onClick={() => setRecording(true)}>Record a payout</button>
          {dist.mercury_request_id ? (
            <ConfirmButton
              label="Refresh"
              disabled={disabled}
              sentence="This checks Mercury for whether Justin has approved the transfer."
              yes="Yes, check"
              onConfirm={() =>
                onAct('Checked with Mercury.', async () => {
                  await call('books.refresh_payout', { month });
                })
              }
            />
          ) : (
            <ConfirmButton
              label="Send via Mercury"
              disabled={disabled}
              sentence={`This asks Mercury to send ${dollars(dist.amount_cents)} to Leah. Justin approves it in the Mercury app before any money moves.`}
              yes="Yes, send the request"
              tone="primary"
              onConfirm={() =>
                onAct('The request is waiting for approval in Mercury.', async () => {
                  await call('books.send_payout', { month });
                })
              }
            />
          )}
        </div>
      ) : (
        <div className="lp-form">
          <div className="lp-form-grid">
            <Field label="Paid on"><input className="lp-inp" type="date" value={date} disabled={disabled} onChange={(e) => setDate(e.target.value)} /></Field>
            <Field label="Amount"><input className="lp-inp" value={dollars(dist.amount_cents)} readOnly /></Field>
            <Field label="How">
              <select className="lp-sel" value={method} disabled={disabled} onChange={(e) => setMethod(e.target.value)}>
                <option>Mercury</option>
                <option>Check</option>
                <option>Zelle</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Reference" hint="Check number, transfer id, or a note."><input className="lp-inp" value={reference} disabled={disabled} onChange={(e) => setReference(e.target.value)} /></Field>
          </div>
          <div className="lp-row">
            <ConfirmButton
              label="Record it"
              tone="primary"
              disabled={disabled}
              sentence={`This marks ${dollars(dist.amount_cents)} to Leah as paid on ${day(date)} and freezes the month.`}
              yes="Yes, record it"
              onConfirm={() =>
                onAct('Payout recorded.', async () => {
                  await call('books.record_payout', { month, payee: dist.payee, method, reference: reference.trim() || null, paid_at: date });
                  setRecording(false);
                })
              }
            />
            <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setRecording(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Year ─────────────────────────────────────────────────────────────────────
type Statement = {
  year: number;
  generated_at: string;
  settings: Record<string, unknown>;
  months: (MonthRow & { allocated_costs_cents: number; adjustments_cents: number; distributable_cents: number })[];
  totals: { gross_cents: number; commission_cents: number; refunds_cents: number; net_cents: number; allocated_costs_cents: number; adjustments_cents: number; distributable_cents: number };
  costs: Cost[];
  costs_by_vendor: Record<string, { amount_cents: number; allocated_cents: number; count: number }>;
  distributions: Dist[];
  distributions_by_member: Record<string, { retained_cents: number; owed_cents: number; paid_cents: number; total_cents: number }>;
  adjustments: Adj[];
  balances: { payee: string; owed_cents: number; paid_cents: number; outstanding_cents: number; retained_cents: number }[];
};

function YearView({ year, setYear }: { year: string; setYear: (y: string) => void }) {
  const [st, setSt] = useState<Statement | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    call<Statement>('books.annual_statement', { year })
      .then((d) => alive && setSt(d))
      .catch((e) => alive && setErr(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [year]);

  const rows = useMemo(() => {
    if (!st) return [];
    const byMonth = new Map(st.months.map((m) => [m.month.slice(0, 7), m]));
    return Array.from({ length: 12 }, (_, i) => {
      const key = `${year}-${String(i + 1).padStart(2, '0')}`;
      const m = byMonth.get(key) ?? null;
      const leah = st.distributions.find((d) => d.month.slice(0, 7) === key && d.payee === 'leah') ?? null;
      const laura = st.distributions.find((d) => d.month.slice(0, 7) === key && d.payee === 'laura') ?? null;
      return { key, m, leah, laura };
    });
  }, [st, year]);

  const paid = st?.distributions.filter((d) => d.status === 'paid') ?? [];
  const yearsList = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));

  function csv() {
    if (!st) return;
    const lines: string[][] = [
      [STATEMENT_HEADLINE],
      [`Year ${st.year}`, `Prepared ${day(st.generated_at)}`],
      [],
      ['Month', 'Gross', 'Apple commission', 'Refunds', 'Net', 'Costs (Rootegra share)', 'Adjustments', 'Distributable', 'Leah', 'Laura', 'Leah paid'],
      ...rows.map(({ key, m, leah, laura }) => [
        monthName(`${key}-01`),
        d(m?.gross_cents), d(m?.commission_cents), d(m?.refunds_cents), d(m?.net_cents),
        d(m?.allocated_costs_cents), d(m?.adjustments_cents), d(m?.distributable_cents),
        d(leah?.amount_cents), d(laura?.amount_cents),
        leah ? (leah.status === 'paid' ? `Paid ${day(leah.paid_at)}` : leah.status) : '',
      ]),
      ['Total', d(st.totals.gross_cents), d(st.totals.commission_cents), d(st.totals.refunds_cents), d(st.totals.net_cents), d(st.totals.allocated_costs_cents), d(st.totals.adjustments_cents), d(st.totals.distributable_cents),
        d(st.distributions_by_member.leah?.total_cents), d(st.distributions_by_member.laura?.total_cents), ''],
      [],
      ['Payouts'],
      ['Month', 'Payee', 'Amount', 'Paid on', 'Method', 'Reference'],
      ...paid.map((p) => [monthName(p.month), p.payee === 'leah' ? 'Leah' : 'Laura', d(p.amount_cents), day(p.paid_at), p.method ?? '', p.reference ?? '']),
      [],
      ['Costs by vendor'],
      ['Vendor', 'Bills', 'Whole amount', 'Rootegra share'],
      ...Object.entries(st.costs_by_vendor).map(([v, c]) => [vendorWords(v), String(c.count), d(c.amount_cents), d(c.allocated_cents)]),
      [],
      ['Adjustments'],
      ['Month', 'Amount', 'Reason'],
      ...st.adjustments.map((a) => [monthName(a.month), d(a.amount_cents), a.reason]),
      [],
      ['Balances'],
      ['Member', 'Owed', 'Paid', 'Outstanding', 'Retained'],
      ...st.balances.map((b) => [b.payee === 'leah' ? 'Leah' : 'Laura', d(b.owed_cents), d(b.paid_cents), d(b.outstanding_cents), d(b.retained_cents)]),
    ];
    const text = lines.map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rootegra-statement-${st.year}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <>
      <div className="lp-row lp-no-print" style={{ marginBottom: 18 }}>
        <label className="lp-field-label" htmlFor="books-year">Year</label>
        <select id="books-year" className="lp-sel" style={{ width: 'auto' }} value={year} onChange={(e) => setYear(e.target.value)}>
          {yearsList.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" className="lp-btn" disabled={!st} onClick={csv}>Download annual statement</button>
        <button type="button" className="lp-btn" disabled={!st} onClick={() => window.print()}>Print</button>
      </div>

      <ErrorNote error={err} notConfigured="Books are not connected yet." />
      {loading && !st && <Empty>Loading…</Empty>}

      {st && (
        <div className="lp-panel">
          <p style={{ margin: '0 0 4px', fontSize: 17, lineHeight: 1.5 }}><strong>{STATEMENT_HEADLINE}</strong></p>
          <p className="lp-sub" style={{ margin: '0 0 16px' }}>Year {st.year} · prepared {day(st.generated_at)} · member shares Leah {String((st.settings.shares as Record<string, number> | undefined)?.leah ?? 51)}% / Laura {String((st.settings.shares as Record<string, number> | undefined)?.laura ?? 49)}%</p>

          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num">Gross</th>
                  <th className="num">Commission</th>
                  <th className="num">Refunds</th>
                  <th className="num">Net</th>
                  <th className="num">Costs</th>
                  <th className="num">Leah</th>
                  <th className="num">Laura</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ key, m, leah, laura }) => (
                  <tr key={key} className={m ? '' : 'quiet'}>
                    <td>{monthName(`${key}-01`)}</td>
                    <td className="num">{m ? dollars(m.gross_cents) : '—'}</td>
                    <td className="num">{m ? dollars(m.commission_cents) : '—'}</td>
                    <td className="num">{m ? dollars(m.refunds_cents) : '—'}</td>
                    <td className="num">{m ? dollars(m.net_cents) : '—'}</td>
                    <td className="num">{m ? dollars(m.allocated_costs_cents) : '—'}</td>
                    <td className="num">{leah ? dollars(leah.amount_cents) : '—'}</td>
                    <td className="num">{laura ? dollars(laura.amount_cents) : '—'}</td>
                    <td>{leah ? (leah.status === 'paid' ? `Yes, ${day(leah.paid_at)}` : leah.mercury_request_id ? 'Waiting in Mercury' : 'Owed') : m?.closed_at ? '—' : m ? 'Open' : ''}</td>
                  </tr>
                ))}
                <tr className="total">
                  <td>Total</td>
                  <td className="num">{dollars(st.totals.gross_cents)}</td>
                  <td className="num">{dollars(st.totals.commission_cents)}</td>
                  <td className="num">{dollars(st.totals.refunds_cents)}</td>
                  <td className="num">{dollars(st.totals.net_cents)}</td>
                  <td className="num">{dollars(st.totals.allocated_costs_cents)}</td>
                  <td className="num">{dollars(st.distributions_by_member.leah?.total_cents ?? 0)}</td>
                  <td className="num">{dollars(st.distributions_by_member.laura?.total_cents ?? 0)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="lp-section-title" style={{ fontSize: 24, margin: '26px 0 10px' }}>Payouts</h2>
          {paid.length === 0 ? <Empty>No payouts recorded in {st.year}.</Empty> : (
            <div className="lp-tbl-wrap">
              <table className="lp-tbl">
                <thead><tr><th>Month</th><th>To</th><th className="num">Amount</th><th>Paid on</th><th>How</th><th>Reference</th></tr></thead>
                <tbody>
                  {paid.map((p) => (
                    <tr key={p.id}>
                      <td>{monthName(p.month)}</td>
                      <td>{p.payee === 'leah' ? 'Leah' : 'Laura'}</td>
                      <td className="num">{dollars(p.amount_cents)}</td>
                      <td>{day(p.paid_at)}</td>
                      <td>{p.method ?? '—'}</td>
                      <td>{p.reference ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="lp-section-title" style={{ fontSize: 24, margin: '26px 0 10px' }}>Balances</h2>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Member</th><th className="num">Owed</th><th className="num">Paid</th><th className="num">Outstanding</th><th className="num">Retained</th></tr></thead>
              <tbody>
                {st.balances.map((b) => (
                  <tr key={b.payee}>
                    <td>{b.payee === 'leah' ? 'Leah' : 'Laura'}</td>
                    <td className="num">{dollars(b.owed_cents)}</td>
                    <td className="num">{dollars(b.paid_cents)}</td>
                    <td className="num">{dollars(b.outstanding_cents)}</td>
                    <td className="num">{dollars(b.retained_cents)}</td>
                  </tr>
                ))}
                {st.balances.length === 0 && <tr><td colSpan={5} className="muted">Nothing closed yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {Object.keys(st.costs_by_vendor).length > 0 && (
            <>
              <h2 className="lp-section-title" style={{ fontSize: 24, margin: '26px 0 10px' }}>Costs by vendor</h2>
              <div className="lp-tbl-wrap">
                <table className="lp-tbl">
                  <thead><tr><th>Vendor</th><th className="num">Bills</th><th className="num">Whole amount</th><th className="num">Rootegra share</th></tr></thead>
                  <tbody>
                    {Object.entries(st.costs_by_vendor).map(([v, c]) => (
                      <tr key={v}><td>{vendorWords(v)}</td><td className="num">{c.count}</td><td className="num">{dollars(c.amount_cents)}</td><td className="num">{dollars(c.allocated_cents)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {st.adjustments.length > 0 && (
            <>
              <h2 className="lp-section-title" style={{ fontSize: 24, margin: '26px 0 10px' }}>Adjustments</h2>
              <div className="lp-tbl-wrap">
                <table className="lp-tbl">
                  <thead><tr><th>Month</th><th className="num">Amount</th><th>Reason</th></tr></thead>
                  <tbody>
                    {st.adjustments.map((a) => (
                      <tr key={a.id}><td>{monthName(a.month)}</td><td className="num">{dollars(a.amount_cents)}</td><td>{a.reason}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function d(cents: number | null | undefined): string {
  return cents == null ? '' : (cents / 100).toFixed(2);
}
